export const DEEP_DATA_AGENT = 'deepresearch';
export const DEEP_DATA_DAILY_LIMIT = 1;

export type FeatureRestriction = 'login' | 'upgrade' | 'daily-limit';

export function normalizeUserLevel(level: unknown): 'L0' | 'L1' | 'L2' | 'L3' {
  if (typeof level === 'number' && Number.isFinite(level)) {
    return normalizeUserLevel(String(Math.floor(level)));
  }

  if (typeof level !== 'string') {
    return 'L0';
  }

  const normalized = level.trim().toUpperCase();
  if (normalized === 'L0' || normalized === '0' || normalized === 'GUEST') return 'L0';
  if (normalized === 'L1' || normalized === '1' || normalized === 'REGISTERED') return 'L1';
  if (normalized === 'L2' || normalized === '2' || normalized === 'CHALLENGE') return 'L2';
  if (normalized === 'L3' || normalized === '3' || normalized === 'VIP') return 'L3';

  return 'L0';
}

export function canUseDeepDataAccess(level: unknown): boolean {
  const normalizedLevel = normalizeUserLevel(level);
  return normalizedLevel === 'L2' || normalizedLevel === 'L3';
}

export function getDeepDataRestriction(level: unknown, dailyUsed = false): FeatureRestriction | null {
  const normalizedLevel = normalizeUserLevel(level);

  if (normalizedLevel === 'L0') {
    return 'upgrade';
  }

  if (!canUseDeepDataAccess(normalizedLevel)) {
    return 'upgrade';
  }

  return dailyUsed ? 'daily-limit' : null;
}

export function isDeepDataAgent(value: unknown): boolean {
  return value === DEEP_DATA_AGENT;
}

export function getFeatureRestrictionForErrorCode(code: unknown): FeatureRestriction | null {
  if (code === 'feature_requires_login') return 'login';
  if (code === 'feature_upgrade_required') return 'upgrade';
  if (code === 'deep_data_daily_limit_reached') return 'daily-limit';

  return null;
}

export async function getFeatureRestrictionFromResponse(response: Response): Promise<FeatureRestriction | null> {
  if (response.ok) return null;

  try {
    const payload = (await response.json()) as { error?: { code?: unknown } };
    return getFeatureRestrictionForErrorCode(payload?.error?.code);
  } catch {
    return null;
  }
}

export function getDeepDataUsageDate(date = new Date()): string {
  return date.toISOString().slice(0, 10).replace(/-/g, '');
}

export function getDeepDataUsageStorageKey(userId: unknown, date = new Date()): string {
  const safeUserId = typeof userId === 'string' && userId.trim() ? userId.trim() : 'anonymous';
  return `aifo.deepData.${safeUserId}.${getDeepDataUsageDate(date)}`;
}

export function getDeepDataUsageCookieName(userId: string, date = new Date()): string {
  return `aifo_deep_data_${userId}_${getDeepDataUsageDate(date)}`;
}

export function getSecondsUntilNextUtcDay(date = new Date()): number {
  const nextDay = Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate() + 1, 0, 0, 0);
  return Math.max(1, Math.ceil((nextDay - date.getTime()) / 1000));
}
